import React, {useState, useMemo} from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { LoadingDots } from '../../../common/loading_dots';
import PhotoTagModal from '../components/photo_tag_update_modal';
import DisplayAsset from '../../display_asset';
import cx from 'classnames';
import { 
    NHAssetType,
    IUpdateSequencePayLoad,
    IPortalPropertyPhotoAsset,
    IPortalPropertyTagObj,
    IPortalPropertyPhotoObj,
    IPortalPropertyTagModalData
} from '../../../../interfaces/asset';
import { IMessageType } from '../../../../interfaces/common';
import { updateProfileCompleteness } from '../../../../utils/profile_completeness';
import { TagKeyNameIcon } from '../../../tag_keyname_icon';
import { StarredAssetToggle } from '../components/starred_asset_toggle';
import { showConfirmationDialog, showErrorDialog } from '../helpers';
import { useUpdatePhotoSequence } from '../hooks/useUpdatePhotoSequence';
import { useDeletePropertyPhoto } from '../hooks/useDeletePropertyPhoto';
import { useGetPropertiesPhotos } from '../hooks/useGetPropertiesPhotos';
import { AddPhotosButton } from '../components/add_photos_button';
import PhotoCaptionModal  from '../components/photo_caption_update_modal';
import { useMessages } from '../../../../pages/property_media/property-photos/messages_context';
import { StarringInstructions } from '../components/starring_instructions';

interface IProps {
    selectedNHID: number | null;
    nhIDs: number[];
}

const IndividualPropertyPhotos = ({
    selectedNHID,
    nhIDs,
}: IProps) => {
    const [ photoCaptionModal, setPhotoCaptionModal ] = useState<IPortalPropertyPhotoObj | null>(null);
    const [ tagUpdateModal, setTagUpdateModal ] = useState<IPortalPropertyTagModalData | null>(null);    

    const { messages, clearMessages } = useMessages();
    const nhID = selectedNHID || nhIDs[0];
    const { data = [], isLoading } = useGetPropertiesPhotos([nhID], { 
        // in individual property photo pages, allow all to be seen (by setting profileCompletnessFilterValue to null) 
        // regardless of profile completeness filter state
        profileCompletenessFilterValue: null 
    });
    const { mutate: updateSequenceOrder } = useUpdatePhotoSequence();
    const { mutate: deletePropertyPhoto } = useDeletePropertyPhoto(nhID);

    const publishedEntityCount = useMemo(() => {
        const selectedProperty = data.find((property: any) => property.NHID === selectedNHID) || data[0];
        if (selectedProperty) {
            const tags = selectedProperty.Tag.reduce((tagCount: number, tag:IPortalPropertyTagObj) => tag.IsTagPublishWebsite ? ++tagCount : tagCount, 0);
            const photos = selectedProperty.Tag.reduce((photoCount: number, tag:IPortalPropertyTagObj) => tag.IsTagPublishWebsite ? photoCount + tag.Photo.length : photoCount, 0);
            return { tags, photos };
        }
    }, [data]);
   
    const handleOnDragEnd = async (result: DropResult) =>  {
        const { destination, source} = result;
        
        if (!destination) {
            return;
        }
    
        if(
            destination.droppableId === source.droppableId && 
            destination.index === source.index
        ) {
            return;
        }

        const reorderItems = (propertyTags:IPortalPropertyTagObj[]) => {
            const tagMap = new Map<number, IPortalPropertyPhotoObj[]>;
            propertyTags.forEach((tagItem: IPortalPropertyTagObj) => {
                tagMap.set(tagItem.TagID, tagItem.Photo);
            });

            const tagIdOfDraggedPhoto = parseInt(source.droppableId.split('-')[1], 10);
            const currentListOfPhotosForTag = tagMap.get(tagIdOfDraggedPhoto);

            const draggedPhoto = currentListOfPhotosForTag[source.index];
            const newlyOrderedListOfPhotosForTag = [
                ...currentListOfPhotosForTag.slice(0, source.index),
                ...currentListOfPhotosForTag.slice(source.index + 1),
            ];
            newlyOrderedListOfPhotosForTag.splice(destination.index, 0, draggedPhoto);

            tagMap.set(tagIdOfDraggedPhoto, newlyOrderedListOfPhotosForTag);

            const tagArray = Array.from(tagMap.values()).flat(1);

            return tagArray.map((item: IPortalPropertyPhotoObj) => item.AssetID);
        };
    
        if( destination.droppableId === source.droppableId ) {
           
                const [propertyDetailsWithPhotosAndTags]  = data.filter((propertyPhoto:IPortalPropertyPhotoAsset) => propertyPhoto.NHID === nhID);
     
                const sequenceOrderedAssetIDs = reorderItems(propertyDetailsWithPhotosAndTags.Tag);
        
                const params: IUpdateSequencePayLoad = {
                    AssetIDs: sequenceOrderedAssetIDs,
                    NHID: nhID
                };

                await updateSequenceOrder(params, { 
                    onError: () =>  showErrorDialog('Failed to update order of the photo.') 
                });
        }
    };


    const handleDeletePhoto = async (assetID: number) => {
        const isConfirmed = await showConfirmationDialog({
            title: 'Delete Photo', 
            message: 'Are you sure you want to delete this photo?'
        });

        if (isConfirmed.value) {
            await deletePropertyPhoto(assetID, {
                onSuccess: () => updateProfileCompleteness(),
                onError: () => showErrorDialog('Failed to delete the photo.')
            });
        }
    };

    const renderIndividualPropertyPhoto = () => {
        const property = data.find((photo: any) => photo.NHID === selectedNHID) || data[0];
        return (
            <>
                 {
                    property ?
                    <> 
                        <div className="flex flex-col items-center text-center gap-3 md:flex-row md:text-start">
                            <strong className="text-md md:text-lg lg:text-xl lg:whitespace-nowrap">
                                {property.Property.Name} has {publishedEntityCount.photos} photos uploaded using {publishedEntityCount.tags} tags
                            </strong>
                            <div className="md:ms-auto">
                                <AddPhotosButton 
                                    propertyName={property.Property.Name} 
                                    propertyNhID={property.Property.NHID} 
                                    propertySlug={property?.Property?.Slug}
                                    />
                            </div>
                        </div>
                        <StarringInstructions />
                        {
                            messages.map((photoUploadMessage: IMessageType, index: number) => {
                                return (
                                    <div className="" key={index}>
                                        <span key={`message-${photoUploadMessage.type}`} className={cx('msg', photoUploadMessage.type === 'info' ? 'msg-info' : 'msg-error mt-1')}>
                                            {photoUploadMessage.message}
                                        </span>
                                    </div>
                                );
                            })
                        }
                        <div className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 grid gap-3 sm:gap-5 md:gap-6 2xl:gap-7">
                            { property.Tag.map((tag:IPortalPropertyTagObj) => {
                                    return ( tag.Photo.length > 0 ?
                                        <div className="ring-1 ring-brand_grey overflow-hidden rounded-md lg:rounded-lg flex flex-col" key={tag.TagName}>
                                            <div className="bg-brand_faint-blue px-3 sm:px-4 2xl:px-5 py-3 min-h-14 flex flex-col items-start xl:items-center xl:justify-between gap-2 xl:flex-row h-full">
                                                <div className={cx('inline-flex gap-1.5 items-center bg-brand_secondary text-white h-8 leading-8 rounded-full text-xs px-2.5 [&>span]:bg-white [&>span]:text-brand_secondary [&>span]:leading-none [&>span]:text-2xs [&>span]:rounded-md [&>span]:py-1 [&>span]:p-1.5', { 'bg-brand_status-error' : !tag.IsTagPublishWebsite })}>
                                                    <TagKeyNameIcon tagKeyName={tag.TagKeyName}/>{tag.TagName} <span>{tag.Photo.length}</span>
                                                </div>
                                                {
                                                    !tag.IsTagPublishWebsite ?
                                                        <span className="msg msg-info !bg-white">
                                                            These photos will not show on the website
                                                        </span> : 
                                                    <span className="text-sm">
                                                        Drag and drop photos to re-order them within each tag
                                                    </span>
                                                }
                                            </div>
                                            <div>
                                                <DragDropContext onDragEnd={handleOnDragEnd}>    
                                                    <Droppable droppableId={`tag-${tag.TagID}`}  direction="horizontal">
                                                        {(provided) => (                                           
                                                            <ul id={`sortable-${tag.TagID}`} 
                                                                className='p-3 sm:p-4 2xl:p-5 gap-3 flex overflow-x-auto scrollbar scrollbar-full scrollbar-media'
                                                                {...provided.droppableProps} 
                                                                ref={provided.innerRef}
                                                            >    
                                                            {
                                                                tag.Photo.map((photo:IPortalPropertyPhotoObj, index:number) => {
                                                                return (
                                                                    <Draggable key={photo.AssetID} draggableId={`drId-${photo.AssetID}`} index={index}>
                                                                        {(provided) => (
                                                                    
                                                                    <li 
                                                                        {...provided.draggableProps} 
                                                                        {...provided.dragHandleProps} 
                                                                        ref={provided.innerRef} 
                                                                        className='max-w-72 bg-white relative'
                                                                    >
                                                                        <DisplayAsset
                                                                            className='object-cover rounded-t-md min-w-72 max-w-72 h-44'
                                                                            TransformedAssetUrl={photo.TransformedAssetUrl}
                                                                            FileName={photo.FileName}
                                                                            AssetType={NHAssetType.Photos} 
                                                                        />
                                                                        <StarredAssetToggle nhID={selectedNHID} assetID={photo.AssetID} assetTypeID={1} isStarred={photo.IsStarred} />
                                                                        { photo.Title ?
                                                                            <div className="p-2 text-2xs border-x border-b border-brand_grey">
                                                                                {photo.Title}
                                                                            </div> : null
                                                                        }
                                                                        <ul className="flex gap-2 justify-between p-2 text-2xs border-x border-b border-brand_grey rounded-b-md mt-auto">
                                                                            <li key={`change-tag-${photo.AssetID}`}>
                                                                            {
                                                                                tag.IsTagPublishWebsite ?
                                                                                <a
                                                                                    className="link"
                                                                                    href=""
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        setTagUpdateModal({
                                                                                            TagID: tag.TagID,
                                                                                            TagName: tag.TagName,
                                                                                            TagSequence: tag.TagSequence,
                                                                                            IsTagPublishWebsite: tag.IsTagPublishWebsite,
                                                                                            IsTagPublishPortal: tag.IsTagPublishWebsite,
                                                                                            IsTagPublishCMS:tag.IsTagPublishCMS,
                                                                                            AssetID: photo.AssetID
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    Change Tag
                                                                                </a>
                                                                                :
                                                                                    ' '
                                                                            }
                                                                            </li>
                                                                            <li key={`edit-caption-${photo.AssetID}`}>
                                                                                <a 
                                                                                    className="link"
                                                                                    href=""
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        setPhotoCaptionModal(photo);
                                                                                    }}
                                                                                >
                                                                                    Edit Caption
                                                                                </a>
                                                                            </li>
                                                                            <li key={`delete-${photo.AssetID}`}>
                                                                                <a 
                                                                                    href=""
                                                                                    className="link"
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleDeletePhoto(photo.AssetID);
                                                                                    }}
                                                                                >
                                                                                    Delete
                                                                                </a>
                                                                            </li>
                                                                        </ul>
                                                                    </li>
                                                                    )}
                                                                    </Draggable>
                                                                );
                                                                })
                                                            }
                                                            {provided.placeholder}    
                                                            </ul>
                                                        )}
                                                    </Droppable>
                                                </DragDropContext>
                                            </div>
                                        </div> : null
                                    );
                                })
                            }
                        </div>
                        {
                            photoCaptionModal ?
                            <PhotoCaptionModal
                                photoDetail={photoCaptionModal}
                                handleClose={async () => {
                                    updateProfileCompleteness();
                                    clearMessages();
                                    setPhotoCaptionModal(null);
                                }
                                }
                                nhID={selectedNHID}
                            />
                            : null
                        }
                        {
                            tagUpdateModal ?
                            <PhotoTagModal
                                sourceTag={tagUpdateModal}
                                handleClose={() => {
                                    updateProfileCompleteness();
                                    clearMessages();
                                    setTagUpdateModal(null);
                                }}
                                nhID={selectedNHID}
                            />
                            : null
                        }
                    </> : null
                }
            </>
        );
    };

    return (
        <>
            <LoadingDots show={isLoading} />
            { renderIndividualPropertyPhoto() }
        </>
    );
};

export default IndividualPropertyPhotos;